/**
 * <ReadmissionRiskWidget> — clinician-dashboard tile (Feb 2026).
 *
 * Surfaces the NEWS2 + LACE Readmission Risk Engine on the clinician
 * dashboard so the "we track vitals to flag 30-day readmission risk"
 * pitch claim is visible the moment they log in. Renders a one-line
 * KPI (high-risk count + total monitored), then the top 3 highest-risk
 * patients with their LACE score + last NEWS2 band + a "Record vitals"
 * inline CTA that opens <VitalsRecordModal>.
 *
 * Reads:
 *   GET /api/clinician/readmission/roster → {count, high_count, roster}
 *
 * Honest framing: validated indices for decision support — never marketed
 * as a guarantee. Empty state surfaces an onboarding nudge instead of a
 * fake "all clear" pill.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from '../../lib/api';
import { Activity, AlertTriangle, ArrowRight, Stethoscope, ClipboardList } from 'lucide-react';
import { TileHeader } from './BentoWidgets';
import VitalsRecordModal from './VitalsRecordModal';
import LaceComputeModal from './LaceComputeModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_STYLE = {
  high: { cls: 'border-red-500/40 bg-red-500/8 text-red-600', label: 'High' },
  moderate: { cls: 'border-cn-accent/40 bg-cn-accent/8 text-cn-accent', label: 'Moderate' },
  low: { cls: 'border-cn-success/40 bg-cn-success/8 text-cn-success', label: 'Low' },
};

const bandPill = (band) => {
  const s = BAND_STYLE[band];
  if (!s) {
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border border-cn-border bg-cn-bg/60 text-cn-text-muted">
        Unscored
      </span>
    );
  }
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${s.cls}`}>
      {s.label}
    </span>
  );
};

export const ReadmissionRiskWidget = ({ getAuthHeader }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [vitalsTarget, setVitalsTarget] = useState(null);
  const [laceTarget, setLaceTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/clinician/readmission/roster`, getAuthHeader?.());
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Unable to load readmission roster.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const roster = data?.roster || [];
  const total = data?.count || 0;
  const highCount = data?.high_count || 0;
  const top = roster.slice(0, 3);

  return (
    <div data-testid="readmission-risk-widget">
      <TileHeader
        eyebrow="Readmission risk"
        title="30-day readmission watchlist"
        action={
          <a
            href="/clinician/readmission-risk"
            className="text-sm font-semibold text-cn-primary inline-flex items-center gap-1.5 hover:underline"
            data-testid="readmission-widget-open"
          >
            Open <ArrowRight className="w-4 h-4" />
          </a>
        }
      />

      {/* Two KPIs: monitored panel size + high-risk count */}
      <div className="grid grid-cols-2 gap-3 mt-5">
        <div className="px-3 py-3 rounded-xl border border-cn-primary/30 bg-cn-primary/8 text-center">
          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Monitored</p>
          <p className="text-2xl font-display font-extrabold text-cn-primary mt-1"
             data-testid="readmission-widget-total">{total}</p>
        </div>
        <div className={`px-3 py-3 rounded-xl text-center ${highCount > 0
          ? 'border border-red-500/40 bg-red-500/10'
          : 'border border-cn-border/60 bg-cn-bg/60'}`}>
          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">High risk</p>
          <p className={`text-2xl font-display font-extrabold mt-1 ${highCount > 0 ? 'text-red-600' : 'text-cn-text-muted'}`}
             data-testid="readmission-widget-high-count">{highCount}</p>
        </div>
      </div>

      <div className="mt-5">
        {loading && <div className="h-24 rounded-xl bg-cn-bg/60 border border-cn-border/60 animate-pulse" />}
        {!loading && err && <p className="text-xs text-cn-text-muted">{err}</p>}

        {!loading && !err && roster.length === 0 && (
          <div className="p-4 rounded-xl border border-dashed border-cn-border/70 bg-cn-bg/60"
               data-testid="readmission-widget-empty">
            <div className="flex items-start gap-3">
              <Stethoscope className="w-5 h-5 text-cn-primary mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-cn-text dark:text-white">
                  No patients on your readmission roster yet
                </p>
                <p className="text-xs text-cn-text-muted mt-1">
                  Once you accept a discharge referral or are assigned a patient, CaraLoom
                  starts tracking NEWS2 + LACE indices to surface 30-day readmission risk.
                </p>
              </div>
            </div>
          </div>
        )}

        {!loading && !err && top.map((p) => (
          <div key={p.patient_id}
               className="p-3 rounded-xl border border-cn-border/70 bg-white dark:bg-cn-ink-800 mb-2"
               data-testid={`readmission-widget-row-${p.patient_id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-cn-text dark:text-white truncate">
                {p.patient_name || `Patient ${p.patient_id.slice(0, 8)}`}
              </p>
              {bandPill(p.combined_band)}
            </div>
            {(p.primary_diagnosis || p.partner_name) && (
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                {p.partner_name && (
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border border-cn-primary/30 bg-cn-primary/8 text-cn-primary"
                        data-testid={`readmission-partner-${p.patient_id}`}>
                    {p.partner_name}
                  </span>
                )}
                {p.primary_diagnosis && (
                  <p className="text-[10px] text-cn-text-muted truncate">{p.primary_diagnosis}</p>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-cn-text-muted mt-1">
              {p.lace?.score != null && (
                <span data-testid={`readmission-lace-${p.patient_id}`}>
                  LACE <span className="font-bold text-cn-text dark:text-white">{p.lace.score}/19</span>
                </span>
              )}
              {p.last_news2_total != null && (
                <span data-testid={`readmission-news2-${p.patient_id}`}>
                  NEWS2 <span className="font-bold text-cn-text dark:text-white">{p.last_news2_total}</span>
                </span>
              )}
              {p.last_vitals_at && (
                <span>vitals · {new Date(p.last_vitals_at).toLocaleDateString()}</span>
              )}
            </div>
            <div className="flex gap-2 mt-2.5">
              <button
                className="cn-btn cn-btn-outline !py-1.5 !px-2.5 !text-[11px] inline-flex items-center gap-1.5"
                onClick={() => setVitalsTarget(p)}
                data-testid={`readmission-record-vitals-${p.patient_id}`}
              >
                <Activity className="w-3 h-3" /> Record vitals
              </button>
              <button
                className="cn-btn cn-btn-outline !py-1.5 !px-2.5 !text-[11px] inline-flex items-center gap-1.5"
                onClick={() => setLaceTarget(p)}
                data-testid={`readmission-compute-lace-${p.patient_id}`}
              >
                <ClipboardList className="w-3 h-3" /> Update LACE
              </button>
            </div>
          </div>
        ))}

        {!loading && !err && highCount > 0 && (
          <div className="mt-3 p-3 rounded-xl border border-red-500/30 bg-red-500/8 flex items-start gap-2"
               data-testid="readmission-widget-high-banner">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-semibold text-red-600">
                {highCount} patient(s) flagged for 30-day readmission outreach
              </p>
              <p className="text-cn-text-muted mt-0.5">
                Validated indices (NEWS2 + LACE) — clinical decision support, not a guarantee.
              </p>
            </div>
          </div>
        )}
      </div>

      {vitalsTarget && (
        <VitalsRecordModal
          open
          patient={vitalsTarget}
          getAuthHeader={getAuthHeader}
          onClose={() => setVitalsTarget(null)}
          onSaved={() => { setVitalsTarget(null); load(); }}
        />
      )}
      {laceTarget && (
        <LaceComputeModal
          open
          patient={laceTarget}
          getAuthHeader={getAuthHeader}
          onClose={() => setLaceTarget(null)}
          onSaved={() => { setLaceTarget(null); load(); }}
        />
      )}
    </div>
  );
};

export default ReadmissionRiskWidget;
